/* L'EMAIL RISING UP — le contenu d'un mailing de groupe, et le HTML qui part.
 *
 * Decision d'Hicham du 02/10/2026 (« B ») : l'editeur Unlayer (externe, en
 * anglais, page vide a chaque email) est remplace par une mise en page FIXE a
 * la charte de la landing. On ne remplit que : titre, texte, bouton.
 *
 * Le serveur ne change pas : il recoit toujours `mailJSON` (ici notre contenu,
 * marque `editeur: "rising-up"`) et `mailHTML`, dans lequel il remplace les
 * champs `{{...}}` au moment de l'envoi (mailingController, processMergeTags).
 *
 * Charte (memoire « Email design », 13/05/2026) : angles droits partout,
 * bouton noir carre, fond creme, pas de photo (l'onglet Promotions de Gmail).
 */

export const EDITEUR = 'rising-up'

/* Les champs personnalises : ce que Nawal lit et tape, et ce que le serveur
   remplace. Les cles `{{...}}` sont celles de mailingController. */
export const CHAMPS = [
  { lisible: '[Prénom et nom]', tag: '{{first_and_last_name}}' },
  { lisible: '[Nom du module]', tag: '{{module_name}}' },
  { lisible: '[Nom du groupe]', tag: '{{groupe_name}}' },
  { lisible: "[Date d'ouverture]", tag: '{{open_date}}' },
  { lisible: '[Date de fermeture]', tag: '{{close_date}}' },
]

export const contenuVide = () => ({ editeur: EDITEUR, version: 1, titre: '', texte: '', bouton: { libelle: '', lien: '' }, signature: '', rubrique: '' })

export const estContenuRisingUp = (json) => !!json && typeof json === 'object' && json.editeur === EDITEUR
/* Un contenu lu (base, modele) : complete avec les valeurs vides, pour qu'un
   champ absent ne fasse pas tomber le dialogue. */
export const contenuComplet = (json) => {
  const v = contenuVide()
  return { ...v, ...json, titre: String(json?.titre ?? ''), texte: String(json?.texte ?? ''), signature: String(json?.signature ?? ''), rubrique: String(json?.rubrique ?? ''),
    bouton: { libelle: String(json?.bouton?.libelle ?? ''), lien: String(json?.bouton?.lien ?? '') } }
}

const echapper = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;')

/* Le texte de Nawal, echappe, avec ses champs lisibles rendus en `{{...}}`,
   quelles que soient la casse et l'apostrophe (droite ou typographique, que
   macOS substitue seule). */
const normaliser = (s) => s.toLowerCase().replace(/[\u2019\u2018`]|&#39;/g, "'")
const avecChamps = (texteEchappe) => texteEchappe.replace(/\[[^\][]{1,40}\]/g, (m) => {
  const c = CHAMPS.find((x) => normaliser(echapper(x.lisible)) === normaliser(m))
  return c ? c.tag : m
})

/* Seuls http et https font un lien : pas de javascript:, pas de mailto: ici. */
export const lienValide = (lien) => /^https?:\/\/[^\s"'<>{}]+$/i.test(String(lien || '').trim())

/* ⛔ LA CHARTE DE LA NEWSLETTER « MATIERE GRISE » (Hicham, 02/10/2026 :
   « reprends la charte de la newsletter pour que ce soit nickel, et une police
   comme Helvetica »). Source : Rising_Up/Marketing/Newsletter/n01/propositions/
   fabriquer.py (gabarit A) : en-tete logo + mention en capitales et filet,
   marges de 44 px, petit titre orange en capitales, titre fin, bouton noir en
   capitales « › », signature de Nawal, pied gris clair. Helvetica partout :
   elle s'affiche pareil dans toutes les messageries, Gmail compris. */
const F = "font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;"
const ENCRE = '#0A0A0B', ORANGE = '#E26438', TEXTE = '#2A2520', DOUX = '#5A544D', MUET = '#8A8480', BORD = '#E8E1D6', CREME = '#F7F3EE'

/* Le logo des emails automatiques (config/emails/risingup.png, 855 x 160),
   publie avec le site sous /email/risingup.png : les relances partent sans
   piece jointe, le logo integre (cid:) n'y apparaitrait pas, et le SVG de la
   newsletter ne s'affiche ni dans Gmail ni dans Outlook. */
export const cheminLogo = '/email/risingup.png'

const para = (html, taille = 16, couleur = TEXTE, marge = '0 0 18px') =>
  `<p style="margin:${marge};${F}font-size:${taille}px;line-height:1.7;color:${couleur};">${html}</p>`
const lignes = (t) => avecChamps(echapper(String(t).trim())).replace(/\n/g, '<br>')

export const genererHtml = (contenu, { origine = '' } = {}) => {
  const titre = avecChamps(echapper(contenu?.titre))
  const rubrique = String(contenu?.rubrique || '').trim()
  const paragraphes = String(contenu?.texte || '')
    .split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean)
    .map((x) => para(lignes(x))).join('')
  const libelle = String(contenu?.bouton?.libelle || '').trim()
  const lien = String(contenu?.bouton?.lien || '').trim()
  const bouton = libelle && lienValide(lien)
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 6px;"><tr><td style="background:${ENCRE};">`
      + `<a href="${echapper(lien)}" style="display:inline-block;padding:15px 30px;${F}font-size:14px;letter-spacing:0.08em;text-transform:uppercase;color:#FFFFFF;text-decoration:none;">${avecChamps(echapper(libelle))}&nbsp;&nbsp;›</a>`
      + `</td></tr></table>`
    : ''
  /* La signature vient APRES le bouton. Sa derniere ligne (« pour toute
     l'equipe Rising Up ») est en plus petit, comme dans la newsletter. */
  const sig = String(contenu?.signature || '').trim().split('\n')
  const signature = sig[0]
    ? para(sig.length > 2
      ? sig.slice(0, -1).map((l) => avecChamps(echapper(l))).join('<br>') + `<br><span style="color:${DOUX};font-size:14px;">${avecChamps(echapper(sig[sig.length - 1]))}</span>`
      : lignes(sig.join('\n')), 16, ENCRE, '28px 0 0')
    : ''
  const logo = lienValide(origine + cheminLogo)
    ? `<img src="${echapper(origine + cheminLogo)}" alt="Rising Up" height="22" style="display:block;height:22px;width:auto;border:0;">`
    : `<span style="${F}font-size:18px;color:${ENCRE};">Rising Up</span>`
  return '<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">'
    + `<title>${titre}</title>`
    + '<style>@media only screen and (max-width:620px){.ru-p{padding-left:24px!important;padding-right:24px!important}}</style></head>'
    + `<body style="margin:0;padding:0;background:${CREME};">`
    + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${CREME};padding:40px 16px;"><tr><td align="center">`
    + `<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:#FFFFFF;border:1px solid ${BORD};">`
    + `<tr><td class="ru-p" style="padding:34px 44px 26px;border-bottom:1px solid #F0EBE3;">`
    + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td>${logo}</td>`
    + `<td align="right" style="${F}font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:${MUET};">THE LAB</td></tr></table>`
    + `</td></tr>`
    + `<tr><td class="ru-p" style="padding:38px 44px 40px;">`
    + (rubrique ? `<p style="margin:0 0 12px;${F}font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:${ORANGE};">${avecChamps(echapper(rubrique))}</p>` : '')
    + (titre ? `<p style="margin:0 0 18px;${F}font-size:24px;line-height:1.2;letter-spacing:-0.015em;color:${ENCRE};">${titre}</p>` : '')
    + paragraphes + bouton + signature
    + '</td></tr>'
    + `<tr><td class="ru-p" style="padding:28px 44px 36px;background:#FAF8F6;border-top:1px solid #F0EBE3;">`
    + para('Rising Up · deeptech française née de la recherche, depuis 2017 · Paris', 12, MUET, '0 0 6px')
    + para('Une question&nbsp;? Réponds simplement à cet email.', 12, MUET, '0')
    + '</td></tr></table></td></tr></table></body></html>'
}

/* L'apercu : les champs remplaces par des exemples, comme le ferait le serveur. */
export const avecExemples = (html, exemples) => CHAMPS.reduce(
  (h, c) => h.split(c.tag).join(echapper(exemples?.[c.tag] ?? c.lisible)), html)

/* ── Les emails et modeles faits avec Unlayer ──────────────────────────────
   Ils partent tels quels (leur HTML est enregistre). Pour les reecrire, on
   recupere leur texte : les blocs texte et titre du design Unlayer. */
/* Le navigateur decode TOUTES les entites (&rsquo;, &eacute;...) une seule fois. */
const decoder = (html) => {
  if (typeof document === 'undefined') return html
  const t = document.createElement('textarea'); t.innerHTML = html; return t.value
}
const sansBalises = (html) => decoder(String(html || '')
  .replace(/<(style|script)[\s\S]*?<\/\1>/gi, '')
  .replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|h[1-6]|li)>/gi, '\n\n')
  .replace(/<[^>]*>/g, ''))
  .replace(/\u00a0/g, ' ')
  .replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()

const enLisible = (t) => CHAMPS.reduce((s, c) => s.split(c.tag).join(c.lisible), t)

export const contenuDepuisUnlayer = (design) => {
  const contenu = contenuVide()
  const blocs = []
  const rangees = Array.isArray(design?.body?.rows) ? design.body.rows : []
  for (const rangee of rangees) {
    for (const colonne of (Array.isArray(rangee?.columns) ? rangee.columns : [])) {
      for (const bloc of (Array.isArray(colonne?.contents) ? colonne.contents : [])) {
        try {   // un bloc illisible est saute, la suite est gardee
          const v = bloc?.values || {}
          const lien = v?.href?.values?.href || v?.href?.url || ''
          if (bloc.type === 'heading' && !contenu.titre) contenu.titre = enLisible(sansBalises(v.text))
          else if (bloc.type === 'button' && !contenu.bouton.libelle) contenu.bouton = { libelle: enLisible(sansBalises(v.text)), lien }
          else if (bloc.type === 'button') blocs.push(enLisible(sansBalises(v.text)) + (lien ? '\u00a0: ' + lien : ''))
          else if (v.text || v.html) blocs.push(enLisible(sansBalises(v.text || v.html)))
        } catch (e) { /* bloc saute */ }
      }
    }
  }
  contenu.texte = blocs.filter(Boolean).join('\n\n')
  return contenu
}

/* ── La date d'envoi ───────────────────────────────────────────────────────
   Le serveur garde deux decalages depuis la date d'ouverture ou de fermeture
   (a minuit) : jours (deltaTiming*) puis heures (deltaTiming*2). L'ecran dit :
   « N jours avant/apres, a H h ». */
export const versChamps = ({ jours, sens, heure }) => ({
  deltaTimingOperator: sens === 'avant' ? '-' : '+',
  deltaTimingNbr: Math.max(0, parseInt(jours, 10) || 0),
  deltaTimingUnit: 'J',
  deltaTimingOperator2: '+',
  deltaTimingNbr2: Math.min(23, Math.max(0, parseInt(heure, 10) || 0)),
  deltaTimingUnit2: 'H',
})

/* Un ancien mailing peut avoir « - 3 H » : on ramene tout en heures, puis en
   « N jours avant/apres, a H h ». */
export const depuisChamps = (m) => {
  const signe = (op) => (op === '-' ? -1 : 1)
  const enHeures = (n, unite) => (parseInt(n, 10) || 0) * (unite === 'H' ? 1 : 24)
  const total = signe(m?.deltaTimingOperator) * enHeures(m?.deltaTimingNbr, m?.deltaTimingUnit)
    + signe(m?.deltaTimingOperator2) * enHeures(m?.deltaTimingNbr2, m?.deltaTimingUnit2 || 'H')
  const jourDecale = Math.floor(total / 24)
  const heure = total - jourDecale * 24
  return { jours: Math.abs(jourDecale), sens: jourDecale < 0 ? 'avant' : 'apres', heure }
}

/* ── Les types d'email : ce que Nawal choisit d'abord ──────────────────────
   Chacun preremplit objet, destinataires, envoi, titre, texte et bouton ;
   tout reste modifiable. Le lien du bouton est la plateforme d'ou l'admin
   ecrit (LAB pour LAB). Textes a relire par Nawal : c'est sa voix. Tutoiement,
   comme le tableau de bord et ses anciens emails, signes de son prenom. */
export const TYPES_EMAIL = [
  {
    cle: 'ouverture', rubrique: "Ton module est ouvert", nom: "Rappel d'ouverture", explication: "Le jour où le module ouvre, à tout le groupe.",
    mailDest: 'all', timingSource: 'openDate', envoi: { jours: 0, sens: 'apres', heure: 9 },
    objet: 'Ton module [Nom du module] est ouvert',
    titre: 'Bonjour [Prénom et nom]',
    texte: "Ton module [Nom du module] est ouvert depuis ce matin.\n\nIl te faut une vingtaine de minutes. Tu peux le quitter et le reprendre quand tu veux : ta progression est enregistrée à chaque écran.\n\nIl reste ouvert jusqu'au [Date de fermeture].",
    bouton: 'Commencer mon module',
  },
  {
    cle: 'pasCommence', rubrique: "Ton module t'attend", nom: 'Relance : pas commencé', explication: "3 jours après l'ouverture, à ceux qui n'ont pas commencé.",
    mailDest: 'notStarted', timingSource: 'openDate', envoi: { jours: 3, sens: 'apres', heure: 9 },
    objet: "Ton module t'attend",
    titre: 'Bonjour [Prénom et nom]',
    texte: "Ton module [Nom du module] est ouvert, et tu ne l'as pas encore commencé.\n\nVingt minutes suffisent pour le premier pas, et tu reprendras là où tu t'es arrêté.",
    bouton: 'Commencer mon module',
  },
  {
    cle: 'enCours', rubrique: "Dernière ligne droite", nom: 'Relance : pas terminé', explication: 'Une semaine avant la fermeture, à ceux qui ont commencé sans finir.',
    mailDest: 'inProgress', timingSource: 'closeDate', envoi: { jours: 7, sens: 'avant', heure: 9 },
    objet: 'Plus que quelques jours pour terminer ton module',
    titre: 'Bonjour [Prénom et nom]',
    texte: "Tu as commencé [Nom du module] : il ferme le [Date de fermeture].\n\nTu reprends exactement là où tu t'es arrêté.",
    bouton: 'Reprendre mon module',
  },
  {
    cle: 'termine', rubrique: "Bravo", nom: 'Félicitations', explication: "Le jour de la fermeture, à ceux qui ont terminé.",
    mailDest: 'ended', timingSource: 'closeDate', envoi: { jours: 0, sens: 'apres', heure: 10 },
    objet: 'Bravo, tu as terminé [Nom du module]',
    titre: 'Bravo [Prénom et nom]',
    texte: 'Tu as terminé [Nom du module].\n\nTes résultats t\'attendent sur la plateforme.',
    bouton: 'Voir mes résultats',
  },
  {
    cle: 'libre', nom: 'Email libre', explication: 'Une page vide, à remplir vous-même.',
    mailDest: 'all', timingSource: 'openDate', envoi: { jours: 0, sens: 'apres', heure: 9 },
    objet: '', titre: '', texte: '', bouton: '',
  },
]

/* L'objet garde les champs lisibles, mais un objet d'email n'est pas du HTML :
   le serveur n'y remplace rien. On le remplit donc avec les vraies valeurs. */
export const objetAvecValeurs = (objet, valeurs) => CHAMPS.reduce(
  (o, c) => o.split(c.lisible).join(valeurs?.[c.tag] ?? ''), String(objet || ''))

export const preremplir = (type, { origine = '', valeurs = {} } = {}) => ({
  name: type.cle === 'libre' ? '' : type.nom,
  mailObject: objetAvecValeurs(type.objet, valeurs),
  mailDest: type.mailDest,
  timingSource: type.timingSource,
  envoi: { ...type.envoi },
  contenu: { ...contenuVide(), titre: type.titre, texte: type.texte, bouton: { libelle: type.bouton, lien: type.bouton ? origine : '' },
    signature: type.cle === 'libre' ? '' : "À très vite,\nNawal Abboub,\npour toute l'équipe Rising Up",
    rubrique: type.rubrique || '' },
})

/* ── Ce qui part au serveur (pur, teste) ───────────────────────────────────
   - un email fait avec Unlayer et non reecrit : ni mailJSON ni mailHTML (le
     serveur fait un UPDATE partiel : son contenu reste intact en base) ;
   - les six champs de date ne partent que si l'envoi a change : un ancien
     reglage (« - 1 J, - 3 H ») ne doit pas etre reecrit par une simple
     sauvegarde (relecture de code du 02/10). */
export const envoiChange = (existant, { envoi, timingSource }) => !existant
  || timingSource !== existant.timingSource
  || JSON.stringify(depuisChamps(existant)) !== JSON.stringify({ jours: envoi.jours, sens: envoi.sens, heure: envoi.heure })

export const corpsDeRequete = ({ existant, groupeID, contentID, name, mailObject, mailDest, timingSource, envoi, finalTiming, ancienFormat, contenu, origine }) => ({
  ...(existant ? { id: existant.id } : {}),
  groupeID, contentID, name, mailObject, mailDest,
  ...(envoiChange(existant, { envoi, timingSource }) ? { timingSource, ...versChamps(envoi), finalTiming } : {}),
  ...(ancienFormat ? {} : { mailJSON: JSON.stringify(contenu), mailHTML: genererHtml(contenu, { origine }) }),
})
